import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { fmtDateCompact } from '../lib/dates';
import { formatGBP as gbp } from '../lib/money';
import { StatCard, CARD_SHELL, SectionHeader, RefreshControl } from './reporting/MetricCard';

// Admin-only Briefs Reporting dashboard. The Briefs page gates this
// behind isAdmin() so non-admins see ReportingAccessDenied instead;
// the dashboard itself trusts that gating and just renders.
//
// Server-side enforcement is still required — any future /api endpoint
// serving reporting aggregates MUST check the requester's role and 403
// non-admins. Client-side gating alone is not sufficient.
//
// Data sources:
//   • briefs[] prop — full set, including archived. Already loaded by
//     the Briefs page for List + Board, so we reuse it.
//   • brief_responses (lazy) — fetched on mount; gives supplier_id per
//     submitted response so we can compute supplier win rates.
//   • suppliers (lazy) — fetched on mount; gives supplier metadata for
//     names, categories, compliance, active flag, created_at.
//
// All money figures honour the post-Phase-C convention: awarded_amount
// is the ex-VAT NET figure entered at close time.

const TIMEFRAMES = [
  { value: '7D',  label: '7D',  days: 7 },
  { value: '30D', label: '30D', days: 30 },
  { value: '90D', label: '90D', days: 90 },
  { value: 'YTD', label: 'YTD' },
  { value: 'All', label: 'All' },
];

const STATUS_BUCKETS = [
  { key: 'draft',    label: 'Drafts',       color: '#51534A' },
  { key: 'sent',     label: 'Sent',         color: '#92D400' },
  { key: 'response', label: 'Responses In', color: '#2E8FA1' },
  { key: 'awarded',  label: 'Awarded',      color: '#009159' },
  { key: 'closed',   label: 'Closed',       color: '#2a2a2a' },
];

const timeframeStart = (tf) => {
  if (tf === 'All') return null;
  const now = new Date();
  if (tf === 'YTD') return new Date(now.getFullYear(), 0, 1);
  const days = TIMEFRAMES.find(t => t.value === tf)?.days;
  if (!days) return null;
  const d = new Date(now); d.setDate(d.getDate() - days);
  return d;
};

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};

const currentQuarterStart = () => {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  return new Date(now.getFullYear(), q * 3, 1);
};

const formatGBPShort = (n) => gbp(n, { fallback: '£0' });

const daysBetween = (a, b) => {
  if (!a || !b) return null;
  const ms = new Date(b).getTime() - new Date(a).getTime();
  if (!isFinite(ms)) return null;
  return ms / 86400000;
};

const bucketForBrief = (b) => {
  if (b.archived_at) return null;
  if (b.status === 'cancelled') return 'closed';
  if (b.status === 'draft') return 'draft';
  if (b.status === 'sent') return (b.response_count || 0) > 0 ? 'response' : 'sent';
  if (b.status === 'closed') return b.awarded_supplier_id ? 'awarded' : 'closed';
  return null;
};

// Last 12 calendar weeks ending today (Mon-Sun-ish; we don't anchor to
// Monday, we just slice 7-day buckets back from now). Each bucket has a
// half-open [start, end) range used for filtering.
const buildWeekBuckets = (n) => {
  const buckets = [];
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  end.setDate(end.getDate() + 1);
  for (let i = 0; i < n; i++) {
    const bucketEnd = new Date(end);
    bucketEnd.setDate(bucketEnd.getDate() - i * 7);
    const bucketStart = new Date(bucketEnd);
    bucketStart.setDate(bucketStart.getDate() - 7);
    buckets.unshift({ start: bucketStart, end: bucketEnd });
  }
  return buckets;
};

// Pretty-print "Updated Xm ago". Keeps the toolbar's freshness signal
// honest without a ticking timer — we only re-evaluate on render.
const updatedAgo = (asOf) => {
  if (!asOf) return '—';
  const sec = Math.floor((Date.now() - asOf.getTime()) / 1000);
  if (sec < 60) return 'Updated just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `Updated ${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `Updated ${hr} hr ago`;
  const day = Math.floor(hr / 24);
  return `Updated ${day} day${day === 1 ? '' : 's'} ago`;
};

// Container cards (charts, lists, composites) share the one shell primitive;
// the clickable stat cards below use <StatCard> from the same module.
const cardClass = CARD_SHELL;
const cardLabel = "fusion-field-label";

export default function BriefReporting({ briefs, currentInternalUser, onDrillDown, timeframe = 'All' }) {
  // Timeframe is owned by the Reporting page header now — one control drives
  // both tabs, and the smart default (90D vs All by data density) lives there.
  // This component just consumes the value.
  const [asOf, setAsOf] = useState(new Date());
  const [, setTick] = useState(0);

  const [responses, setResponses] = useState([]);
  const [supplierMap, setSupplierMap] = useState(new Map());
  const [loadingExtras, setLoadingExtras] = useState(true);

  // Lazy-fetch dashboard-only data so the Briefs page's primary fetch
  // (List/Board) isn't slowed by reporting joins. Manual refresh from
  // the toolbar reuses the same path and refreshes the asOf stamp.
  const fetchExtras = async () => {
    setLoadingExtras(true);
    const [{ data: respRows }, { data: supRows }] = await Promise.all([
      supabase
        .from('brief_responses')
        .select('brief_id, supplier_id, is_submitted, submitted_at')
        .eq('is_submitted', true),
      supabase
        .from('suppliers')
        .select('id, supplier_name, category, compliance_status, status, created_at'),
    ]);
    setResponses(respRows || []);
    const map = new Map();
    for (const s of supRows || []) map.set(s.id, s);
    setSupplierMap(map);
    setAsOf(new Date());
    setLoadingExtras(false);
  };

  useEffect(() => {
    fetchExtras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tick once a minute so the "Updated X min ago" line stays honest
  // without forcing a re-render storm.
  useEffect(() => {
    const id = setInterval(() => setTick(v => v + 1), 60000);
    return () => clearInterval(id);
  }, []);

  // -------------------- Derived: timeframe scope --------------------
  const tfStart = timeframeStart(timeframe);
  const inTimeframe = (date) => {
    if (!tfStart || !date) return tfStart == null;
    return new Date(date) >= tfStart;
  };

  // Briefs visible to "live state" cards — not archived, regardless of
  // when they were created. Drives the Live Briefs and Pipeline donut.
  const liveBriefs = useMemo(
    () => briefs.filter(b => !b.archived_at && b.status !== 'cancelled'),
    [briefs]
  );

  // Briefs in the active timeframe (by created_at). Archived briefs ARE
  // included — archiving doesn't undo the historical fact that a brief
  // was sent, responded to, awarded, or paid for. Live-state widgets
  // (liveBriefs, needsAttentionCount, pipelineSegments) keep their own
  // !archived_at gate above; this window drives the historical
  // aggregates only.
  const inWindow = useMemo(
    () => briefs.filter(b => inTimeframe(b.created_at)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [briefs, timeframe]
  );

  // -------------------- At a Glance --------------------

  const now = new Date();
  const needsAttentionCount = useMemo(
    () => briefs.filter(b =>
      b.status === 'sent'
      && !b.archived_at
      && b.required_response_time
      && new Date(b.required_response_time) < now
      && (b.response_count || 0) === 0
    ).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [briefs]
  );

  const responseRate = useMemo(() => {
    const everSent = inWindow.filter(b => b.status === 'sent' || b.status === 'closed');
    const withResponse = everSent.filter(b => (b.response_count || 0) > 0);
    return {
      percent: everSent.length > 0 ? Math.round((withResponse.length / everSent.length) * 100) : 0,
      withResponse: withResponse.length,
      total: everSent.length,
    };
  }, [inWindow]);

  const avgTimeToAward = useMemo(() => {
    const awarded = inWindow.filter(b => b.status === 'closed' && b.awarded_supplier_id && b.closed_at);
    if (awarded.length === 0) return { days: null, count: 0 };
    const total = awarded.reduce((acc, b) => acc + (daysBetween(b.created_at, b.closed_at) || 0), 0);
    return { days: total / awarded.length, count: awarded.length };
  }, [inWindow]);

  const liveBreakdown = useMemo(() => {
    const out = { awaiting: 0, readyToAward: 0, drafted: 0, total: 0 };
    for (const b of liveBriefs) {
      if (b.status === 'closed') continue;
      out.total++;
      if (b.status === 'draft') out.drafted++;
      else if (b.status === 'sent' && (b.response_count || 0) === 0) out.awaiting++;
      else if (b.status === 'sent' && (b.response_count || 0) > 0) out.readyToAward++;
    }
    return out;
  }, [liveBriefs]);

  // 5-state donut. Counts every non-archived, non-cancelled brief —
  // gives an honest snapshot of where work sits across the pipeline.
  const pipelineSegments = useMemo(() => {
    const counts = { draft: 0, sent: 0, response: 0, awarded: 0, closed: 0 };
    for (const b of liveBriefs) {
      const k = bucketForBrief(b);
      if (k && counts[k] != null) counts[k]++;
    }
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    let cumulative = 0;
    const circ = 2 * Math.PI * 42;
    const segs = STATUS_BUCKETS.map(s => {
      const count = counts[s.key];
      const len = total === 0 ? 0 : (count / total) * circ;
      const offset = cumulative;
      cumulative += len;
      return { ...s, count, len, offset };
    });
    return { segs, total, circ };
  }, [liveBriefs]);

  const briefsSentWindows = useMemo(() => {
    // Archived briefs count toward send activity — they were sent at some
    // point and archiving is a curation action, not a retraction.
    const sent = briefs.filter(b => b.status === 'sent' || b.status === 'closed');
    const since = (n) => sent.filter(b => new Date(b.created_at) >= daysAgo(n)).length;
    return { d7: since(7), d30: since(30), d90: since(90), d365: since(365) };
  }, [briefs]);

  // -------------------- Trends & Insights --------------------

  const awardedInWindow = useMemo(
    () => inWindow.filter(b => b.status === 'closed' && b.awarded_supplier_id),
    [inWindow]
  );

  const spendAwarded = useMemo(() => {
    const total = awardedInWindow.reduce((acc, b) => acc + (Number(b.awarded_amount) || 0), 0);
    const avg = awardedInWindow.length > 0 ? total / awardedInWindow.length : 0;

    const weeks = buildWeekBuckets(12);
    const weekly = weeks.map(w => {
      const sum = awardedInWindow
        .filter(b => b.closed_at && new Date(b.closed_at) >= w.start && new Date(b.closed_at) < w.end)
        .reduce((acc, b) => acc + (Number(b.awarded_amount) || 0), 0);
      return { ...w, sum };
    });
    const max = Math.max(1, ...weekly.map(w => w.sum));
    return { total, avg, count: awardedInWindow.length, weekly, max };
  }, [awardedInWindow]);

  const spendByCategory = useMemo(() => {
    const map = new Map();
    let total = 0;
    for (const b of awardedInWindow) {
      const c = (b.brief_category || '').trim() || 'Uncategorised';
      const amt = Number(b.awarded_amount) || 0;
      map.set(c, (map.get(c) || 0) + amt);
      total += amt;
    }
    const rows = Array.from(map.entries())
      .map(([name, amount]) => ({ name, amount, percent: total > 0 ? (amount / total) * 100 : 0 }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 6);
    const max = Math.max(1, ...rows.map(r => r.amount));
    return { rows, total, max };
  }, [awardedInWindow]);

  // Supplier win rates: per supplier, count submitted responses in
  // window (quoted) and count wins in window (awarded). Minimum 3
  // quotes to qualify — otherwise a 1-of-1 supplier dominates with 100%.
  const topSuppliers = useMemo(() => {
    const briefIdsInWindow = new Set(inWindow.map(b => b.id));
    const quotedBy = new Map();
    for (const r of responses) {
      if (!briefIdsInWindow.has(r.brief_id)) continue;
      if (!r.supplier_id) continue;
      quotedBy.set(r.supplier_id, (quotedBy.get(r.supplier_id) || 0) + 1);
    }
    const wonBy = new Map();
    for (const b of awardedInWindow) {
      wonBy.set(b.awarded_supplier_id, (wonBy.get(b.awarded_supplier_id) || 0) + 1);
    }
    const rows = [];
    for (const [supplierId, quoted] of quotedBy.entries()) {
      if (quoted < 3) continue;
      const won = wonBy.get(supplierId) || 0;
      const sup = supplierMap.get(supplierId);
      rows.push({
        supplierId,
        name: sup?.supplier_name || 'Unknown supplier',
        category: sup?.category || 'Uncategorised',
        compliance: sup?.compliance_status || null,
        quoted,
        won,
        winRate: (won / quoted) * 100,
      });
    }
    rows.sort((a, b) => b.winRate - a.winRate || b.won - a.won);
    return rows.slice(0, 5);
  }, [responses, awardedInWindow, supplierMap, inWindow]);

  const supplierHealth = useMemo(() => {
    const recipientIds = new Set();
    for (const b of inWindow) {
      for (const r of (b.brief_recipients || [])) {
        if (r?.supplier_id) recipientIds.add(r.supplier_id);
      }
    }
    // "Active" mirrors how the Suppliers list defines it — status === 'active'.
    // Anything else (inactive, paused, missing record) counts as inactive so the
    // two cells always add up to the unique-recipients total above them.
    let active = 0, inactive = 0;
    for (const id of recipientIds) {
      const s = supplierMap.get(id);
      if (!s) { inactive++; continue; }
      if (s.status === 'active') active++; else inactive++;
    }
    const qStart = currentQuarterStart();
    let newThisQuarter = 0;
    for (const s of supplierMap.values()) {
      if (s.created_at && new Date(s.created_at) >= qStart) newThisQuarter++;
    }
    // Over-reliance: any single supplier holding > 40% of awarded
    // briefs in window. Pulls supplier name from the win map.
    const wonBy = new Map();
    for (const b of awardedInWindow) {
      wonBy.set(b.awarded_supplier_id, (wonBy.get(b.awarded_supplier_id) || 0) + 1);
    }
    const totalWins = awardedInWindow.length;
    const overReliance = [];
    if (totalWins > 0) {
      const sorted = Array.from(wonBy.entries()).sort((a, b) => b[1] - a[1]);
      const [topId, topCount] = sorted[0] || [];
      const pct = topCount ? (topCount / totalWins) * 100 : 0;
      if (pct > 40) {
        const name = supplierMap.get(topId)?.supplier_name || 'a supplier';
        overReliance.push({
          tone: 'amber',
          text: `${name} won ${topCount} of ${totalWins} briefs (${Math.round(pct)}%)`,
          // Specific supplier → straight to their detail page.
          drillTarget: { kind: 'supplier', supplierId: topId },
        });
      }
    }
    // Thin-recipient categories: any category with ≥3 sent briefs but
    // ≤2 unique recipient suppliers — suggests routing on autopilot.
    const sentInWindow = inWindow.filter(b => b.status === 'sent' || b.status === 'closed');
    const byCategory = new Map();
    for (const b of sentInWindow) {
      const c = (b.brief_category || '').trim();
      if (!c) continue;
      if (!byCategory.has(c)) byCategory.set(c, { briefs: 0, suppliers: new Set() });
      const bucket = byCategory.get(c);
      bucket.briefs++;
      for (const r of (b.brief_recipients || [])) {
        if (r?.supplier_id) bucket.suppliers.add(r.supplier_id);
      }
    }
    for (const [cat, info] of byCategory.entries()) {
      if (info.briefs >= 3 && info.suppliers.size <= 2) {
        overReliance.push({
          tone: 'amber',
          text: `${info.briefs} ${cat} briefs went to only ${info.suppliers.size} supplier${info.suppliers.size === 1 ? '' : 's'}`,
          // Drill to the briefs list filtered by that category — quickest
          // path to "show me what those briefs were and who got them".
          drillTarget: { category: cat, banner: { tone: 'amber', descriptor: `in ${cat} · concentrated on a small supplier set` } },
        });
      }
    }
    if (newThisQuarter > 0) {
      overReliance.push({
        tone: 'green',
        text: `${newThisQuarter} new supplier${newThisQuarter === 1 ? '' : 's'} added this quarter`,
        // Drill to filtered Suppliers list — same target as the New This
        // Quarter cell in the breakdown row.
        drillTarget: {
          kind: 'suppliers_filtered',
          suppliersDrill: {
            createdSince: currentQuarterStart().toISOString(),
            banner: { tone: 'green', descriptor: 'added this quarter' },
          },
        },
      });
    }
    return {
      uniqueTotal: recipientIds.size,
      recipientIds: Array.from(recipientIds),
      active, inactive, newThisQuarter,
      overReliance,
    };
  }, [inWindow, supplierMap, awardedInWindow]);

  const briefsByOwner = useMemo(() => {
    const map = new Map();
    for (const b of inWindow) {
      const primary = b.owner_id;
      const fallback = primary || b.owners_list?.[0]?.id || null;
      if (!fallback) continue;
      const name = b.owner?.full_name || b.owners_list?.find(o => o.id === fallback)?.full_name || 'Unknown';
      if (!map.has(fallback)) map.set(fallback, { ownerId: fallback, name, count: 0 });
      map.get(fallback).count++;
    }
    const rows = Array.from(map.values()).sort((a, b) => b.count - a.count);
    const total = rows.reduce((acc, r) => acc + r.count, 0);
    const max = Math.max(1, ...rows.map(r => r.count));
    // Suppress the rebalancing flag when there's only one owner in the
    // data — "100% concentration" reads as sarcasm when there's nobody
    // else to share the load with.
    const topImbalance = rows[0] && rows.length > 1 && total > 0 && (rows[0].count / total) > 0.4
      ? { name: rows[0].name, percent: Math.round((rows[0].count / total) * 100) }
      : null;
    return { rows: rows.slice(0, 6), total, max, topImbalance };
  }, [inWindow]);

  // -------------------- Click handlers --------------------

  const drill = (payload) => onDrillDown?.(payload);

  // Single source of truth for what each donut segment drills to — used by
  // both the SVG segment circles and the legend rows so they always agree.
  const segmentDrill = (key) => {
    if (key === 'draft')    return drill({ status: 'draft', banner: { tone: 'grey', descriptor: 'currently in Draft' } });
    if (key === 'sent')     return drill({ status: 'sent', responseFilter: 'without', banner: { tone: 'teal', descriptor: 'sent and still awaiting a response' } });
    if (key === 'response') return drill({ status: 'sent', responseFilter: 'with', banner: { tone: 'teal', descriptor: 'sent with responses in' } });
    if (key === 'awarded')  return drill({ status: 'closed', awardStateFilter: 'awarded', banner: { tone: 'green', descriptor: 'awarded' } });
    if (key === 'closed')   return drill({ status: 'closed', awardStateFilter: 'closed_no_award', banner: { tone: 'grey', descriptor: 'closed without award' } });
  };

  // ===== Render =====

  return (
    // Width + padding come from the shared page container (reporting-aligned §1);
    // the Admin-view pill now renders once in the shared header.
    <div className="font-['Metropolis']">

        {/* Toolbar — refresh + updated stamp (timeframe lives in the page header). */}
        <div className="flex items-center justify-end gap-3 flex-wrap pb-5 border-b border-line mb-6">
          <RefreshControl updatedLabel={updatedAgo(asOf)} onRefresh={fetchExtras} busy={loadingExtras} />
        </div>

        {/* ===== AT A GLANCE ===== */}
        <SectionHeader label="At a Glance" sub="For team leads · what needs attention now" />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3.5 mb-3.5">
          {/* Needs Attention — the only card that always reads as urgent;
              the handler swaps in the amber drill banner shortcut. */}
          <StatCard
            label="Needs Attention"
            ariaLabel="Needs attention — briefs past deadline with no response"
            onClick={() => drill({ kind: 'needs_attention' })}>
            <p className={`text-4xl font-extrabold leading-none mb-2 ${needsAttentionCount > 0 ? 'text-[#7A4E10]' : 'text-ink'}`}>
              {needsAttentionCount}
            </p>
            <p className="text-xs text-fusion-mid-grey">briefs past deadline with no response</p>
            {needsAttentionCount > 0 && (
              <span className="inline-flex items-center gap-1.5 mt-2 bg-[#E89A2E]/10 text-[#7A4E10] border border-[#E89A2E]/30 rounded-full px-2.5 py-1 text-[10px] font-bold tracking-[0.12em] uppercase">
                <span aria-hidden="true">⚠</span> Action required
              </span>
            )}
          </StatCard>

          {/* Response Rate — whole card drills to the denominator (all
              sent). The "X" and "Y" inside the sub-text are individually
              clickable: X → with-responses subset, Y → same as card. */}
          <StatCard
            label="Response Rate"
            ariaLabel="Response rate — drill to currently sent briefs"
            onClick={() => drill({ status: 'sent', banner: { tone: 'teal', descriptor: 'currently sent · response-rate denominator' } })}>
            <p className="text-4xl font-extrabold text-fusion-forest leading-none mb-2">{responseRate.percent}%</p>
            <p className="text-xs text-fusion-mid-grey">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); drill({ status: 'sent', responseFilter: 'with', banner: { tone: 'teal', descriptor: 'sent that received a response' } }); }}
                className="font-semibold text-ink hover:text-active-green underline-offset-2 hover:underline transition-colors">
                {responseRate.withResponse}
              </button>
              {' of '}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); drill({ status: 'sent', banner: { tone: 'teal', descriptor: 'currently sent · response-rate denominator' } }); }}
                className="font-semibold text-ink hover:text-active-green underline-offset-2 hover:underline transition-colors">
                {responseRate.total}
              </button>
              {' sent briefs received a response'}
            </p>
          </StatCard>

          {/* Avg Time to Award — clickable only when populated; inert when
              empty so the dash + "no data yet" copy do the explanation work
              without offering a useless click target. */}
          {(() => {
            const populated = avgTimeToAward.days != null;
            const body = (
              <>
                <div className="flex items-center justify-between mb-3.5">
                  <span className={cardLabel}>Avg Time to Award</span>
                </div>
                <p className={`text-4xl font-extrabold leading-none mb-2 ${populated ? 'text-fusion-forest' : 'text-[#51534A] opacity-40'}`}>
                  {populated ? avgTimeToAward.days.toFixed(1) : '—'}
                  {populated && <span className="text-base text-[#51534A] font-semibold ml-2">days</span>}
                </p>
                <p className="text-xs text-fusion-mid-grey">
                  {populated
                    ? `From sent to awarded · across ${avgTimeToAward.count} completed brief${avgTimeToAward.count === 1 ? '' : 's'}.`
                    : 'No completed briefs in this window yet.'}
                </p>
              </>
            );
            if (!populated) return <StatCard>{body}</StatCard>;
            return (
              <StatCard
                ariaLabel="Average time to award — drill to awarded briefs, fastest first"
                onClick={() => drill({
                  status: 'closed',
                  awardStateFilter: 'awarded',
                  sort: { field: 'time_to_award', direction: 'asc' },
                  banner: { tone: 'green', descriptor: 'awarded · sorted by time-to-award (fastest first)' },
                })}>
                {body}
              </StatCard>
            );
          })()}

          {/* Live Briefs — card click filters to everything not closed
              (draft + sent). Each segment of the sub-text drills to the
              specific slice it counts. */}
          <StatCard
            label="Live Briefs"
            ariaLabel="Live briefs — drill to draft and sent"
            onClick={() => drill({ statuses: ['draft', 'sent'], banner: { tone: 'teal', descriptor: 'currently live (draft + sent)' } })}>
            <p className="text-4xl font-extrabold text-ink leading-none mb-2">{liveBreakdown.total}</p>
            <p className="text-xs text-fusion-mid-grey">in flight right now</p>
            <p className="text-[11px] text-[#51534A] mt-3 pt-3 border-t border-line">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); drill({ status: 'sent', responseFilter: 'without', banner: { tone: 'teal', descriptor: 'sent and awaiting a response' } }); }}
                className="font-semibold hover:text-ink underline-offset-2 hover:underline transition-colors">
                {liveBreakdown.awaiting} awaiting responses
              </button>
              {' · '}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); drill({ status: 'sent', responseFilter: 'with', banner: { tone: 'teal', descriptor: 'sent with responses in — ready to award' } }); }}
                className="font-semibold hover:text-ink underline-offset-2 hover:underline transition-colors">
                {liveBreakdown.readyToAward} ready to award
              </button>
              {' · '}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); drill({ status: 'draft', banner: { tone: 'grey', descriptor: 'currently in Draft' } }); }}
                className="font-semibold hover:text-ink underline-offset-2 hover:underline transition-colors">
                {liveBreakdown.drafted} drafted
              </button>
            </p>
          </StatCard>
        </div>

        {/* Pipeline donut + Briefs Sent windows */}
        <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-3.5 mb-3.5">

          {/* Pipeline Composition */}
          <div className={cardClass}>
            <div className="flex items-center justify-between mb-3.5">
              <span className={cardLabel}>Pipeline Composition</span>
            </div>
            <div className="grid grid-cols-[auto_1fr] gap-5 items-center">
              <div className="relative w-[110px] h-[110px]">
                <svg viewBox="0 0 100 100" width="110" height="110">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="#2a2a2a" strokeWidth="12" />
                  {pipelineSegments.total > 0 && pipelineSegments.segs.map(s => (
                    s.count > 0 && (
                      <circle
                        key={s.key}
                        cx="50" cy="50" r="42" fill="none"
                        stroke={s.color}
                        strokeWidth="12"
                        strokeDasharray={`${s.len} ${pipelineSegments.circ}`}
                        strokeDashoffset={-s.offset}
                        transform="rotate(-90 50 50)"
                        onClick={() => segmentDrill(s.key)}
                        style={{ cursor: 'pointer' }}>
                        <title>{s.label} · {s.count}</title>
                      </circle>
                    )
                  ))}
                </svg>
                {/* Centre TOTAL is clickable — opens List with no filters
                    (drill banner notes it came from Pipeline). Constrained
                    to the hollow centre so it doesn't overlay segments. */}
                <button
                  type="button"
                  onClick={() => drill({ banner: { tone: 'teal', descriptor: 'in pipeline (all non-archived briefs)' } })}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[72px] h-[72px] rounded-full flex flex-col items-center justify-center hover:opacity-85 transition-opacity bg-transparent"
                  aria-label={`Open ${pipelineSegments.total} pipeline briefs`}>
                  <span className="text-2xl font-extrabold text-ink leading-none">{pipelineSegments.total}</span>
                  <span className="text-[9px] font-bold tracking-[2px] uppercase text-[#51534A] mt-1">Total</span>
                </button>
              </div>
              <div className="flex flex-col gap-2">
                {pipelineSegments.segs.map(s => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => segmentDrill(s.key)}
                    className="grid grid-cols-[12px_1fr_auto] gap-2.5 items-center text-left hover:opacity-85 transition-opacity">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
                    <span className="text-[10px] font-bold tracking-[0.1em] uppercase text-fusion-mid-grey">{s.label}</span>
                    <span className="text-sm font-bold text-ink">{s.count}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Briefs Sent by window — each cell drills to status sent/closed
              within that lookback window (created_at). statuses=['sent','closed']
              because the count includes briefs that have since closed; they
              still belong to "ever sent in this window". */}
          <div className={cardClass}>
            <div className="flex items-center justify-between mb-3.5">
              <span className={cardLabel}>Briefs Sent</span>
              <span className="text-[10px] font-bold tracking-[1.5px] uppercase text-[#51534A]">By window</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4">
              {[
                { label: '7D',   value: briefsSentWindows.d7,   days: 7   },
                { label: '30D',  value: briefsSentWindows.d30,  days: 30  },
                { label: '90D',  value: briefsSentWindows.d90,  days: 90  },
                { label: '365D', value: briefsSentWindows.d365, days: 365 },
              ].map((w, idx, arr) => (
                <button
                  key={w.label}
                  type="button"
                  onClick={() => drill({
                    statuses: ['sent', 'closed'],
                    dateRange: { start: daysAgo(w.days), end: new Date(), field: 'created_at', label: `last ${w.days} days` },
                    banner: { tone: 'teal', descriptor: `sent in the last ${w.days} days` },
                  })}
                  className={`pr-3 text-left hover:opacity-85 transition-opacity ${idx < arr.length - 1 ? 'border-r border-line' : ''} ${idx > 0 ? 'pl-3' : ''}`}>
                  <p className="text-[9px] font-bold tracking-[2px] uppercase text-[#51534A] mb-2">{w.label}</p>
                  <p className="text-2xl font-extrabold text-ink leading-none">{w.value}</p>
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => drill({
                statuses: ['sent', 'closed'],
                dateRange: { start: daysAgo(30), end: new Date(), field: 'created_at', label: 'last 30 days' },
                banner: { tone: 'teal', descriptor: 'sent in the last 30 days' },
              })}
              className="text-[11px] text-[#51534A] mt-4 pt-4 border-t border-line w-full text-left hover:text-fusion-mid-grey transition-colors">
              {briefsSentWindows.d30 > 0
                ? `${briefsSentWindows.d30} brief${briefsSentWindows.d30 === 1 ? '' : 's'} sent in the last 30 days.`
                : 'No briefs sent in the last 30 days.'}
            </button>
          </div>
        </div>

        {/* ===== TRENDS & INSIGHTS ===== */}
        <SectionHeader label="Trends & Insights" sub="For directors · what's happening this quarter" withTopRule />

        {/* Spend Awarded + Spend by Category */}
        <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-3.5 mb-3.5">

          {/* Spend Awarded */}
          <div className={cardClass}>
            <div className="flex items-center justify-between mb-3.5">
              <span className={cardLabel}>Spend Awarded</span>
              <span className="text-[10px] font-bold tracking-[1.5px] uppercase text-[#51534A]">Net · ex-VAT</span>
            </div>
            {spendAwarded.count === 0 ? (
              <p className="text-sm text-fusion-mid-grey py-2">
                <strong className="text-fusion-forest font-bold mr-2">{formatGBPShort(0)}</strong>
                <span className="text-[#51534A]">· No awards in this window yet</span>
              </p>
            ) : (
              <>
                <div className="flex items-baseline gap-3 mb-1">
                  <button
                    type="button"
                    onClick={() => drill({
                      status: 'closed',
                      awardStateFilter: 'awarded',
                      sort: { field: 'awarded_amount', direction: 'desc' },
                      banner: { tone: 'green', descriptor: 'awarded · sorted by awarded amount (highest first)' },
                    })}
                    className="text-4xl font-extrabold text-fusion-forest leading-none hover:opacity-85 transition-opacity">
                    {formatGBPShort(spendAwarded.total)}
                  </button>
                </div>
                <p className="text-xs text-fusion-mid-grey mb-4">
                  Across {spendAwarded.count} awarded brief{spendAwarded.count === 1 ? '' : 's'} · avg {formatGBPShort(spendAwarded.avg)} per brief
                </p>
                <div className="flex items-end gap-1.5 h-[150px] mt-3">
                  {spendAwarded.weekly.map((w, i) => {
                    const heightPct = (w.sum / spendAwarded.max) * 100;
                    const muted = w.sum === 0;
                    const weekLabel = fmtDateCompact(w.start);
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => drill({
                          status: 'closed',
                          awardStateFilter: 'awarded',
                          dateRange: { start: w.start, end: w.end, field: 'closed_at', label: `week of ${weekLabel}` },
                          banner: { tone: 'green', descriptor: `awarded in the week of ${weekLabel}` },
                        })}
                        disabled={w.sum === 0}
                        className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end group hover:opacity-85 transition-opacity disabled:cursor-default disabled:hover:opacity-100">
                        <span className="text-[9px] font-semibold text-fusion-mid-grey opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                          {formatGBPShort(w.sum)}
                        </span>
                        <div
                          className={`w-full rounded-t-sm transition-colors ${muted ? 'bg-[#92D400]/20' : 'bg-[#92D400]'}`}
                          style={{ height: `${Math.max(heightPct, w.sum > 0 ? 4 : 0)}%`, minHeight: w.sum > 0 ? '3px' : '0' }} />
                        <span className="text-[9px] font-semibold text-[#51534A] uppercase tracking-[0.1em]">W{i + 1}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Spend by Category */}
          <div className={cardClass}>
            <div className="flex items-center justify-between mb-3.5">
              <span className={cardLabel}>Spend by Category</span>
            </div>
            {spendByCategory.rows.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center text-[#51534A] min-h-[220px]">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="opacity-50 mb-2.5">
                  <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
                  <path d="M22 12A10 10 0 0 0 12 2v10z" />
                </svg>
                <p className="text-xs italic">No awarded spend in this window</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3 mt-3">
                {spendByCategory.rows.map(r => (
                  <button
                    key={r.name}
                    type="button"
                    onClick={() => drill({
                      category: r.name,
                      status: 'closed',
                      awardStateFilter: 'awarded',
                      banner: { tone: 'green', descriptor: `awarded in ${r.name}` },
                    })}
                    className="grid grid-cols-[110px_1fr_88px] gap-3 items-center text-left hover:opacity-85 transition-opacity">
                    <span className="text-xs font-semibold text-ink truncate" title={r.name}>{r.name}</span>
                    <span className="h-2 bg-white/[0.04] rounded-full overflow-hidden">
                      <span className="block h-full bg-[#92D400] rounded-full" style={{ width: `${(r.amount / spendByCategory.max) * 100}%` }} />
                    </span>
                    <span className="text-xs font-bold text-ink text-right whitespace-nowrap">
                      {formatGBPShort(r.amount)}
                      <span className="text-[10px] text-[#51534A] font-normal ml-1.5">{Math.round(r.percent)}%</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Top Suppliers + Supplier Health */}
        <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-3.5 mb-3.5">

          {/* Top Suppliers by Win Rate */}
          <div className={cardClass}>
            <div className="flex items-center justify-between mb-3.5 gap-2">
              <span className={cardLabel}>Top Suppliers by Win Rate</span>
              <span
                title="Suppliers with fewer than 3 quotes in the window are excluded so a single quote-and-win doesn't dominate the ranking with 100%."
                className="inline-flex items-center text-[9px] font-bold tracking-[1.5px] uppercase text-[#51534A] border border-line rounded-full px-2.5 py-0.5 whitespace-nowrap">
                Min 3 quotes
              </span>
            </div>
            {loadingExtras ? (
              <p className="text-sm text-[#51534A] italic py-4">Loading supplier data…</p>
            ) : topSuppliers.length === 0 ? (
              <p className="text-sm text-[#51534A] italic py-4">Not enough quotes in this window to rank suppliers (need 3+ per supplier).</p>
            ) : (
              <>
                <div className="grid grid-cols-[22px_1fr_70px_70px_80px] gap-3.5 py-2 border-b border-line text-[9px] font-bold tracking-[2px] uppercase text-[#51534A]">
                  <span></span>
                  <span>Supplier</span>
                  <span className="text-right">Quoted</span>
                  <span className="text-right">Won</span>
                  <span className="text-right">Win %</span>
                </div>
                {topSuppliers.map((s, i) => {
                  const winColor = s.winRate >= 60 ? 'text-fusion-forest' : s.winRate < 30 ? 'text-[#7A4E10]' : 'text-ink';
                  const openDetail = () => drill({ kind: 'supplier', supplierId: s.supplierId });
                  return (
                    // role=button + onClick on the row instead of a real
                    // <button> so the per-cell drills inside can be real
                    // buttons themselves (nested <button> is invalid HTML).
                    <div
                      key={s.supplierId}
                      role="button"
                      tabIndex={0}
                      onClick={openDetail}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetail(); } }}
                      className="grid grid-cols-[22px_1fr_70px_70px_80px] gap-3.5 py-3 border-b border-line last:border-b-0 items-center text-left hover:bg-white/[0.02] transition-colors w-full cursor-pointer">
                      <span className="text-[11px] font-bold text-[#51534A] text-center">{i + 1}</span>
                      <span className="min-w-0">
                        <span className="block text-[13px] font-bold text-ink truncate">{s.name}</span>
                        <span className="block text-[9px] font-bold tracking-[0.15em] uppercase text-[#51534A]">
                          {s.category}
                          {s.compliance && <> · <span style={{
                            color: s.compliance === 'green' ? '#009159' : s.compliance === 'amber' ? '#E89A2E' : s.compliance === 'red' ? '#E0290A' : '#51534A',
                          }}>{s.compliance.charAt(0).toUpperCase() + s.compliance.slice(1)} compliance</span></>}
                        </span>
                      </span>
                      {/* Quoted cell drills to the briefs list scoped to
                          briefs this supplier responded to. */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); drill({
                          respondingSupplierFilter: s.supplierId,
                          banner: { tone: 'teal', descriptor: `${s.name} responded to` },
                        }); }}
                        title={`Show briefs ${s.name} responded to`}
                        className="text-xs font-semibold text-ink text-right hover:text-active-green underline-offset-2 hover:underline transition-colors">
                        {s.quoted}
                      </button>
                      {/* Won cell drills to the briefs list scoped to
                          briefs awarded to this supplier. */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); drill({
                          status: 'closed',
                          awardStateFilter: 'awarded',
                          awardedSupplierFilter: s.supplierId,
                          banner: { tone: 'green', descriptor: `awarded to ${s.name}` },
                        }); }}
                        disabled={s.won === 0}
                        title={s.won > 0 ? `Show briefs awarded to ${s.name}` : 'No wins to drill into'}
                        className={`text-xs font-semibold text-right transition-colors ${s.won > 0 ? 'text-fusion-forest hover:opacity-85 underline-offset-2 hover:underline' : 'text-[#51534A] cursor-default'}`}>
                        {s.won}
                      </button>
                      {/* Win % — same target as the row click, but
                          explicitly clickable to match the user spec. */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openDetail(); }}
                        title={`Open ${s.name}`}
                        className={`text-xs font-semibold text-right hover:opacity-85 transition-opacity ${winColor}`}>
                        {Math.round(s.winRate)}%
                      </button>
                    </div>
                  );
                })}
                {/* Escape hatch — useful when admins want the broader
                    supplier list (not just those in the win-rate top 5). */}
                <button
                  type="button"
                  onClick={() => drill({ kind: 'all_suppliers' })}
                  className="mt-3 self-start text-[10px] font-bold tracking-[2px] uppercase text-fusion-forest hover:opacity-85 transition-opacity inline-flex items-center gap-1">
                  View all suppliers →
                </button>
              </>
            )}
          </div>

          {/* Supplier Health */}
          <div className={cardClass}>
            <div className="flex items-center justify-between mb-3.5">
              <span className={cardLabel}>Supplier Health</span>
            </div>
            {/* Headline number — drills to the Suppliers list restricted
                to just the set who received briefs in this window. */}
            <button
              type="button"
              onClick={() => drill({
                kind: 'suppliers_filtered',
                suppliersDrill: {
                  idAllowlist: supplierHealth.recipientIds,
                  banner: { tone: 'teal', descriptor: 'who received briefs this period' },
                },
              })}
              disabled={supplierHealth.uniqueTotal === 0}
              className="text-4xl font-extrabold text-ink leading-none mb-1 hover:opacity-85 transition-opacity disabled:cursor-default disabled:hover:opacity-100">
              {supplierHealth.uniqueTotal}
            </button>
            <p className="text-xs text-fusion-mid-grey mb-3.5">unique suppliers received briefs this period</p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3.5 pt-3.5 border-t border-line">
              {/* Each cell drills to the Suppliers list with the matching
                  filter. Active / Inactive narrow further to the same
                  recipient-id set; New This Quarter is a company-wide
                  cohort (independent of the briefs window). */}
              <button
                type="button"
                onClick={() => drill({
                  kind: 'suppliers_filtered',
                  suppliersDrill: {
                    idAllowlist: supplierHealth.recipientIds,
                    statusPill: 'active',
                    banner: { tone: 'teal', descriptor: 'active and receiving briefs' },
                  },
                })}
                disabled={supplierHealth.active === 0}
                className="text-left hover:opacity-85 transition-opacity disabled:cursor-default disabled:hover:opacity-100">
                <p className="text-xl font-extrabold text-ink leading-none">{supplierHealth.active}</p>
                <p className="text-[9px] font-bold tracking-[0.15em] uppercase text-[#51534A] mt-1">Active<br />Suppliers</p>
              </button>
              <button
                type="button"
                onClick={() => drill({
                  kind: 'suppliers_filtered',
                  suppliersDrill: {
                    idAllowlist: supplierHealth.recipientIds,
                    statusPill: 'inactive',
                    banner: { tone: 'grey', descriptor: 'inactive but received briefs in window' },
                  },
                })}
                disabled={supplierHealth.inactive === 0}
                className="text-left hover:opacity-85 transition-opacity disabled:cursor-default disabled:hover:opacity-100">
                <p className="text-xl font-extrabold text-ink leading-none">{supplierHealth.inactive}</p>
                <p className="text-[9px] font-bold tracking-[0.15em] uppercase text-[#51534A] mt-1">Inactive<br />Suppliers</p>
              </button>
              <button
                type="button"
                onClick={() => drill({
                  kind: 'suppliers_filtered',
                  suppliersDrill: {
                    createdSince: currentQuarterStart().toISOString(),
                    banner: { tone: 'green', descriptor: 'added this quarter' },
                  },
                })}
                disabled={supplierHealth.newThisQuarter === 0}
                className="text-left hover:opacity-85 transition-opacity disabled:cursor-default disabled:hover:opacity-100">
                <p className="text-xl font-extrabold text-ink leading-none">{supplierHealth.newThisQuarter}</p>
                <p className="text-[9px] font-bold tracking-[0.15em] uppercase text-[#51534A] mt-1">New This<br />Quarter</p>
              </button>
            </div>

            {supplierHealth.overReliance.length > 0 && (
              <div className="mt-4 pt-4 border-t border-line">
                <p className="text-[10px] font-bold tracking-[2px] uppercase text-[#51534A] mb-2.5">Over-reliance Watch</p>
                <div className="flex flex-col gap-1.5">
                  {supplierHealth.overReliance.map((w, i) => {
                    const className = `inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold tracking-[0.12em] uppercase w-fit border ${
                      w.tone === 'amber'
                        ? 'bg-[#E89A2E]/[0.08] text-[#7A4E10] border-[#E89A2E]/30'
                        : 'bg-[#92D400]/[0.08] text-fusion-forest border-[#92D400]/30'
                    }`;
                    const inner = (
                      <>
                        <span aria-hidden="true">{w.tone === 'amber' ? '⚠' : '✓'}</span>
                        {w.text}
                      </>
                    );
                    return w.drillTarget ? (
                      <button
                        key={i}
                        type="button"
                        onClick={() => drill(w.drillTarget)}
                        className={`${className} hover:opacity-85 transition-opacity text-left`}>
                        {inner}
                      </button>
                    ) : (
                      <span key={i} className={className}>{inner}</span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Briefs by Owner */}
        <div className={cardClass}>
          <div className="flex items-center justify-between mb-3.5">
            <span className={cardLabel}>Briefs by Owner</span>
          </div>
          {briefsByOwner.rows.length === 0 ? (
            <p className="text-sm text-[#51534A] italic py-4">No briefs in this window yet.</p>
          ) : (
            <div className="flex flex-col gap-3 mt-2">
              {briefsByOwner.rows.map((r, i) => (
                <button
                  key={r.ownerId}
                  type="button"
                  onClick={() => drill({
                    ownerId: r.ownerId,
                    banner: { tone: 'teal', descriptor: `owned by ${r.name}` },
                  })}
                  className="grid grid-cols-[140px_1fr_80px] gap-3 items-center text-left hover:opacity-85 transition-opacity">
                  <span className="text-xs font-semibold text-ink truncate" title={r.name}>{r.name}</span>
                  <span className="h-2 bg-white/[0.04] rounded-full overflow-hidden">
                    <span className="block h-full rounded-full" style={{
                      // Cap visual width at 92% so the top bar never shouts to the
                      // edge of the track — especially noticeable when a single
                      // owner holds 100% of the briefs in the window.
                      width: `${Math.min(92, (r.count / briefsByOwner.max) * 92)}%`,
                      background: i === 0 ? '#92D400' : '#2E8FA1',
                    }} />
                  </span>
                  <span className="text-xs font-bold text-ink text-right">
                    {r.count}
                    <span className="text-[10px] text-[#51534A] font-normal ml-1.5">
                      {briefsByOwner.total > 0 ? `· ${Math.round((r.count / briefsByOwner.total) * 100)}%` : `brief${r.count === 1 ? '' : 's'}`}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {briefsByOwner.topImbalance && (
            <span className="inline-flex items-center gap-1.5 mt-4 bg-[#E89A2E]/[0.08] text-[#7A4E10] border border-[#E89A2E]/30 rounded-full px-2.5 py-1 text-[10px] font-bold tracking-[0.12em] uppercase w-fit">
              <span aria-hidden="true">⚠</span>
              {briefsByOwner.topImbalance.name} owns {briefsByOwner.topImbalance.percent}% of briefs — consider rebalancing
            </span>
          )}
        </div>
    </div>
  );
}

